import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Flex,
  ModalCloseButton,
  Spinner,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
} from '@chakra-ui/react';
import { samplesApi } from '@texas/api/endpoints/samplesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { request } from '@texas/utils/helpers/httpHelpers';
import { SamplesTable } from './SamplesTable';
import { ErrorDetails } from '../alert/ErrorDetails';
import { SharedDisclosureProps } from '../types';
import { TexasFormLabel } from '../form/TexasFormLabel';
import { TexasDatePicker } from '../datePicker/TexasDatePicker';

interface Props extends SharedDisclosureProps {
  sampleIds: number[];
  onPerformed: () => void;
}

export function SetSamplesReadyDateModal(props: Props) {
  const { t } = useTranslation();

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('sample.setReadyDate')}</ModalHeader>
        <ModalCloseButton />
        <Inner
          sampleIds={props.sampleIds}
          onPerform={() => {
            props.onPerformed();
            props.onClose();
          }}
        />
      </ModalContent>
    </Modal>
  );
}

function Inner({
  sampleIds,
  onPerform,
}: {
  sampleIds: number[];
  onPerform: () => void;
}) {
  const { t } = useTranslation();
  const [readyDate, setReadyDate] = useState<Date | null>(null);

  const {
    data: samples,
    refetch: refetchSamples,
    loading: samplesLoading,
    error,
  } = useApiResource(samplesApi.fromIds);

  const {
    request: mainReq,
    loading: mainReqLoading,
    error: mainReqError,
  } = useApiRequest(samplesApi.updateReadyDate);

  useEffect(() => {
    refetchSamples(sampleIds);
  }, [refetchSamples, sampleIds]);

  const req = () => {
    if (readyDate === null) {
      return;
    }
    request(mainReq, [{ sampleIds, readyDate: readyDate }], () => onPerform());
  };

  return (
    <>
      <ModalBody>
        <Flex flexDir="column" gap={4} w="full">
          {error && <ErrorDetails error={error} />}
          {mainReqError && <ErrorDetails error={mainReqError} />}
          <FormControl>
            <TexasFormLabel>{t('sample.expectedReadyDate')}</TexasFormLabel>
            <TexasDatePicker
              selectedDate={readyDate}
              onDateChange={setReadyDate}
              isClearable={false}
            />
          </FormControl>
          {samplesLoading && <Spinner />}
          {!samplesLoading && <SamplesTable samples={samples ?? []} />}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          isLoading={mainReqLoading}
          disabled={readyDate === null || mainReqLoading}
          onClick={() => req()}
        >
          {t('general.save')}
        </Button>
      </ModalFooter>
    </>
  );
}
